/**
 * Integrytas Group — locale display helpers.
 *
 * Turns a CSM locale code (e.g. 'EN_GB') into a flag emoji and a human language
 * name. The flag is derived from the region subtag; the name is localised to the
 * active UI language via Intl.DisplayNames so it reads naturally in any locale.
 */

import { csmLngTag, csmLocaleToI18n, CSM_BCP47, i18n } from './index';

/**
 * CSM locale 'EN_US' → BCP-47 'en-US'.
 *
 * Splitting on '_' covers the LL_CC codes, but not a locale whose real tag
 * carries a variant subtag — 'EN_GB_SCOT' would split to 'en-GB' and lose the
 * Scottish part entirely. Those are registered in CSM_BCP47, which therefore
 * wins over the split.
 */
function toBcp47(code: string): string {
  const tag = csmLngTag(code);
  if (CSM_BCP47[tag]) return CSM_BCP47[tag];
  const [lang, region] = tag.split('_');
  return region ? `${lang.toLowerCase()}-${region.toUpperCase()}` : (lang ?? code).toLowerCase();
}

/**
 * The three country flags Unicode gives to subdivisions rather than countries,
 * keyed by the BCP-47 variant that identifies them. Without this, Scottish
 * English shows the Union flag — correct for its region subtag, but it makes
 * the row indistinguishable from plain British English in a language picker.
 */
const SUBDIVISION_FLAGS: Record<string, string> = {
  scotland: 'gbsct',
  england: 'gbeng',
  wales: 'gbwls',
};

/** Flag emoji for a CSM locale, derived from its region subtag (e.g. 'EN_GB' → 🇬🇧). */
export function localeFlag(code: string): string {
  const parts = toBcp47(code).split('-');

  // A subdivision flag is a black-flag base, the subdivision code spelled in
  // tag characters, then the cancel-tag terminator.
  const subdivision = SUBDIVISION_FLAGS[(parts[2] ?? '').toLowerCase()];
  if (subdivision) {
    return `\u{1F3F4}${[...subdivision].map((c) => String.fromCodePoint(0xe0000 + c.charCodeAt(0))).join('')}\u{E007F}`;
  }

  const region = (parts[1] ?? '').toUpperCase();
  if (!/^[A-Z]{2}$/.test(region)) return '🏳️';
  return String.fromCodePoint(...[...region].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}

/**
 * Names for variant subtags, keyed by the variant.
 *
 * Intl.DisplayNames names languages and regions from CLDR, but its coverage of
 * VARIANT subtags depends on the engine's data: Chrome has no name for
 * `scotland` and falls back to shouting the subtag, so `en-GB-scotland` reads
 * "British English (SCOTLAND)" in the browser while Node renders the proper
 * "British English (Scottish Standard English)". A locale should not be named
 * differently depending on which JS engine happens to be rendering it.
 *
 * So the variant is named here and the base language is still named by Intl —
 * the base stays localised ("inglés británico (…)" in Spanish) and only the
 * variant needs a translation key.
 */
const VARIANT_NAMES: Record<string, { key: string; en: string }> = {
  scotland: { key: 'locale.variant.scotland', en: 'Scottish Standard English' },
};

/** Human language name for a CSM locale, localised to the active UI language. */
export function localeName(code: string, uiLocale?: string): string {
  const tag = toBcp47(code);
  const [lang, region, variant] = tag.split('-');
  try {
    const dn = new Intl.DisplayNames([toBcp47(uiLocale ?? code)], { type: 'language' });
    const named = variant ? VARIANT_NAMES[variant.toLowerCase()] : undefined;
    if (!named) return dn.of(tag) ?? code;

    const base = dn.of(region ? `${lang}-${region}` : lang) ?? tag;
    const label = i18n.t(named.key, {
      defaultValue: named.en,
      lng: csmLocaleToI18n(uiLocale ?? code),
    });
    return `${base} (${label})`;
  } catch {
    return code;
  }
}
